import {
  GT, LT, GTE, LTE, EQ, NE,
  GT_EQUIVALENT, LT_EQUIVALENT, GTE_EQUIVALENT, LTE_EQUIVALENT, EQ_EQUIVALENT, NE_EQUIVALENT
} from '@/constants/shared-constants'

const delimiters = [GTE, LTE, GT, LT, NE, EQ]
const delimiterEquivalents = [GTE_EQUIVALENT, LTE_EQUIVALENT, GT_EQUIVALENT, LT_EQUIVALENT, NE_EQUIVALENT, EQ_EQUIVALENT]

export function searchInput (items, validator = (x) => true, mapper = (x) => x) {
  return {
    items,
    validator,
    mapper
  }
}
export function formatArraySearchString (allowedSearchKeys, searchArray, multiValueKeys = []) {
  /**
   * Format search string for search API.
   * if array doesn't contain any of the (delimiters) then add 'search= to that element
   * if array contains any of the (delimiters) then take the string before the (delimiter) and match it with keywordDict and replace it with the value of the key
   * remove all spaces between the (delimiter) sign, dont add '&' to the last element
   * search element if it in the value of the key in keywordDict then replace it with the key
   * @param {array[searchInput]} allowedSearchKeys - The allowed search keys (searchInputs). e.g. { plant: {
   *                                                                               validator: validatePlant,
   *                                                                               mapper: mapPlant,
   *                                                                               items: ['plants','plant']
   *                                                                             }
   *                                                                    }
   * @param {array[string]} searchArray - The search array. e.g. ['plant=plant1',''plant=plant2'', 'work center=work center 1', '123','test']
   * @param {array[string]} [multiValueKeys] - Keys whose repeated values are collected
   *   into an array instead of being joined with a comma. Use this for free text
   *   fields whose values can contain commas themselves, so that the comma is not
   *   ambiguous between "part of the value" and "next value". The API receives one
   *   query parameter per array item. Defaults to none, i.e. the comma joining.
   * @returns {string, array} A formatted search string and an array of error indexes. eg. { searchString: 'plant=plant1,plant2&work_center=work center 1&search=123,test', errorIndexes: [] }
   */

  let searchString = ''
  const errorIndexes = []
  const searchDict = {}
  if (!searchArray || (searchArray.length === 1 && searchArray[0].trim() === '')) {
    return { searchDict, searchString, errorIndexes }
  }
  // Remove empty strings from search array
  searchArray = searchArray.filter(element => element.trim() !== '')
  searchArray.forEach((element) => {
    const delimiter = delimiters.find((delimiter) => element.includes(delimiter))
    if (delimiter) {
      const delimiterEquivalent = delimiterEquivalents[delimiters.indexOf(delimiter)]
      const splitElement = element.split(delimiter)
      const keys = getKeysByValue(allowedSearchKeys, splitElement[0].trim().toLowerCase())
      let value = splitElement[1].trim()
      if (keys.length === 0 || value === '') {
        errorIndexes.push(searchArray.indexOf(element))
      } else {
        keys.forEach((key) => {
          const validator = allowedSearchKeys[key].validator
          const mapper = allowedSearchKeys[key].mapper
          key = key + delimiterEquivalent.slice(0, -1)
          if (validator(value)) {
            value = mapper(value)
            if (multiValueKeys.includes(key)) {
              // Keep the values separate, the comma cannot be used as a
              // separator for this key.
              searchDict[key] = [...(searchDict[key] ?? []), value]
            } else if (searchDict[key]) {
              // if the key is already in the searchDict then add the value to the string with ','
              searchDict[key] += `,${value}`
            } else {
              searchDict[key] = `${value}`
            }
          } else {
            errorIndexes.push(searchArray.indexOf(element))
          }
        })
      }
    } else {
      if (!searchDict.search) {
        searchDict.search = `${element}`
      } else {
        searchDict.search += `,${element}`
      }
    }
  })
  // add the searchDict to the string. KPT-1116: percent-encode the values -
  // the string is spliced into the URL as-is, and an unencoded '+' (phone
  // numbers) is decoded to a space by the backend. Keys are generated tokens
  // and stay unencoded; encoded commas still split server-side after decoding.
  Object.entries(searchDict).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Multi value keys are repeated instead of comma joined.
      value.forEach(item => {
        searchString += `${key}=${encodeURIComponent(item)}&`
      })
    } else {
      searchString += `${key}=${encodeURIComponent(value)}&`
    }
  })
  // remove the last '&' from the string
  searchString = searchString.slice(0, -1)
  return { searchDict, searchString, errorIndexes }
}

export function filterApplicableSearchTags (allowedSearchKeys, searchArray) {
  /**
   * KPT-1117: keep the search tags that still mean something on another tab.
   *
   * Tabs of the same view accept different filter sets (the uap_logs endpoint
   * has no user_email or delivery_date; the SMS one has no country or
   * if_sms_success), so switching tabs used to leave the criteria behind
   * entirely. A tag is kept exactly when formatArraySearchString would resolve
   * its label to a param on the target tab, which means the retained tags can
   * never produce an "unknown key" error afterwards. Matching is alias-based,
   * so concepts the two tabs name differently (Phone Number -> phone_number vs
   * fitter_id, Tracking Number -> tracking_number vs tracking_num) carry over
   * without rewriting the tag.
   *
   * Kept: tags whose label resolves, and keyless free-text tags (they become
   * the `search` param, which every endpoint accepts). A tag whose label
   * resolves but whose value is empty or invalid is also kept - the criterion
   * does apply, and a red tag is visible, removable feedback, whereas dropping
   * it would silently discard something the user typed.
   * Dropped: tags whose label resolves to no param on the target tab.
   *
   * Always returns a NEW array: SearchBoxMulti pushes into the array it is
   * given, so two stores must never share one reference.
   *
   * @param {object} allowedSearchKeys - the target tab's searchInputs
   * @param {array[string]} searchArray - the tags currently displayed
   * @returns {array[string]} the tags to keep, in their original order
   */
  if (!Array.isArray(searchArray)) {
    return []
  }
  return searchArray.filter((element) => {
    if (typeof element !== 'string' || element.trim() === '') {
      return false
    }
    const delimiter = delimiters.find((delimiter) => element.includes(delimiter))
    if (!delimiter) {
      return true
    }
    return getKeysByValue(allowedSearchKeys, element.split(delimiter)[0].trim().toLowerCase()).length > 0
  })
}

function getKeysByValue (object, value) {
/**
 * Get the keys from an object by its value, where the value of the object is an array.
 * @param {object} object - The object to search in.needs to be in { items: ['plants','plant'] } format
 * @param {string} value - The value to search for.
 * @returns {string} The keys of the value in an array.
 */
  return Object.keys(object).filter(key => {
    const items = object[key].items.map(item => item.toLowerCase())
    const found = items.includes(value.toLowerCase())
    return found
  })
}
